import type { ColocaStatusPillProps } from "@/shared/components/data-display/ColocaStatusPill";
import type { OrganizationsTranslations } from "@/shared/lang/interfaces/organizations";

import type {
  CardOnboardingPauseReason,
  CardOnboardingStage,
  OrganizationMemberStatus,
  OrganizationOverview,
  OrganizationOverviewUser,
} from "../types/organizationsAdmin.types";

type CopyKey = keyof OrganizationsTranslations;

export const CARD_STAGE_PILL: Record<CardOnboardingStage, ColocaStatusPillProps["variant"]> = {
  enabled: "success",
  in_review: "info",
  in_progress: "info",
  paused: "warning",
  not_registered: "neutral",
  rejected: "danger",
};

export const CARD_STAGE_LABEL_KEYS: Record<CardOnboardingStage, CopyKey> = {
  enabled: "stage_enabled",
  in_review: "stage_in_review",
  in_progress: "stage_in_progress",
  paused: "stage_paused",
  not_registered: "stage_not_registered",
  rejected: "stage_rejected",
};

export const CARDHOLDER_STAGE_LABEL_KEYS: Record<CardOnboardingStage, CopyKey> = {
  enabled: "holders_enabled",
  in_review: "holders_in_review",
  in_progress: "holders_in_progress",
  paused: "holders_paused",
  not_registered: "holders_not_registered",
  rejected: "holders_rejected",
};

export const PAUSE_REASON_LABEL_KEYS: Record<CardOnboardingPauseReason, CopyKey> = {
  business_identity: "pause_reason_business_identity",
  missing_identity_fields: "pause_reason_missing_identity_fields",
  missing_email: "pause_reason_missing_email",
  missing_address: "pause_reason_missing_address",
  unmappable_state: "pause_reason_unmappable_state",
  missing_client_data: "pause_reason_missing_client_data",
  documents_unavailable: "pause_reason_documents_unavailable",
  already_registered: "pause_reason_already_registered",
  other: "pause_reason_other",
};

export const MEMBER_STATUS_PILL: Record<OrganizationMemberStatus, ColocaStatusPillProps["variant"]> = {
  active: "success",
  pending_verification: "warning",
  inactive: "neutral",
  suspended: "danger",
};

export const MEMBER_STATUS_LABEL_KEYS: Record<OrganizationMemberStatus, CopyKey> = {
  active: "member_status_active",
  pending_verification: "member_status_pending_verification",
  inactive: "member_status_inactive",
  suspended: "member_status_suspended",
};

export const MEMBER_STATUS_COUNT_KEYS: Record<OrganizationMemberStatus, CopyKey> = {
  active: "members_active",
  pending_verification: "members_pending_verification",
  inactive: "members_inactive",
  suspended: "members_suspended",
};

const STAGE_ORDER: readonly CardOnboardingStage[] = [
  "enabled",
  "in_review",
  "in_progress",
  "paused",
  "not_registered",
  "rejected",
];

const MEMBER_STATUS_ORDER: readonly OrganizationMemberStatus[] = [
  "active",
  "pending_verification",
  "inactive",
  "suspended",
];

/** Only the stages someone is in, in the order staff read them. */
export function cardholderStages(
  counts: OrganizationOverview["card_onboarding"]["cardholders"],
): Array<{ stage: CardOnboardingStage; count: number }> {
  return STAGE_ORDER.filter((stage) => counts[stage] > 0).map((stage) => ({ stage, count: counts[stage] }));
}

export function memberStatuses(
  counts: OrganizationOverview["users"]["counts"],
): Array<{ status: OrganizationMemberStatus; count: number }> {
  return MEMBER_STATUS_ORDER.filter((status) => counts[status] > 0).map((status) => ({
    status,
    count: counts[status],
  }));
}

export function lastSignedIn(users: OrganizationOverviewUser[]): OrganizationOverviewUser | null {
  let latest: OrganizationOverviewUser | null = null;
  for (const user of users) {
    if (!user.last_login_at) continue;
    if (!latest?.last_login_at || user.last_login_at > latest.last_login_at) latest = user;
  }
  return latest;
}

export function cardProviderLabelKey(
  provider: OrganizationOverview["card_onboarding"]["provider"],
): CopyKey | null {
  if (provider === "CRYPTO_MATE") return "provider_crypto";
  if (provider === "BLOQUE") return "provider_cards";
  return null;
}
